import BannerImageCarousel from "components/BannerImageCarousel";
import Breadcrumbs from "components/breadcrumbs/Breadcrumbs";
import Navigation from "components/navigation/Navigation";
import TitleComponent from "components/TitleComponent";
import { STARTPAGETITLES } from "InterfacesTypes/enums";
import type { IHowTo, IHowToImage, ITitleText } from "InterfacesTypes/interfaces";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchHowToImages } from "state/reducers/HowToImagesReducer";
import { fetchHowTos } from "state/reducers/HowTosReducer";
import { selectHowToImages, selectHowTos, selectTitleTexts } from "state/selectors/Selectors";
import { getEnvironment } from "utils/utils";

interface ICategoryGroup {
    Category: string;
    Items: IHowTo[];
}

const HowTo = () => {
    const env = getEnvironment();
    const dispatch = useDispatch();
    const titleTexts: ITitleText[] = useSelector(selectTitleTexts);
    const howTos: IHowTo[] = useSelector(selectHowTos);
    const howToImages: IHowToImage[] = useSelector(selectHowToImages);
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
    const [currentHowTo, setCurrentHowTo] = useState<IHowTo | null>(null);
    const [currentHowToImages, setCurrentHowToImages] = useState<IHowToImage[]>([]);

    useEffect(() => {
        dispatch(fetchHowTos());
        dispatch(fetchHowToImages());
    }, [dispatch])

    const howToTitle = titleTexts.find(x => x.TitlePosition === STARTPAGETITLES.HOW_TO_TITLE);

    const groupedHowTosByCategory: ICategoryGroup[] = Object.entries(
        howTos.reduce((acc, item) => {
            (acc[item.Category] ??= []).push({
                Id: item.Id,
                Title: item.Title,
                Category: item.Category,
            });
            return acc;
        }, {} as Record<string, IHowTo[]>)
    ).map(([Category, Items]) => ({
        Category,
        Items: Items.sort((a, b) => a.Title.localeCompare(b.Title)),
    }))
    .sort((a, b) => a.Category.localeCompare(b.Category));

    const handleCategoryClick = (category: string) => {
        setExpandedCategory(expandedCategory === category ? null : category);
    };

    const handleItemClick = (title: string, id: number) => {
        const selectedHowTo = howTos.find(x => x.Id === id);
        setCurrentHowTo(selectedHowTo ?? null);
        setCurrentHowToImages(howToImages
            .filter(x => x.HowToDescriptionsId === id)
            .sort((a, b) => a.ImageOrder - b.ImageOrder));
    };

    return (
        <div className="app-container !max-w-[1280px] min-h-[100%] mb-10!">
            <BannerImageCarousel />
            <div className="bg-[rgba(33,37,41,1)] flex items-center justify-center">
                <Navigation />
            </div>
            <div className="pl-[40px] pr-[40px]">
                <div className="!pl-2 !mt-2">
                    <Breadcrumbs
                        page="HowTo"
                    />
                </div>
                <div>
                    <TitleComponent
                        title={howToTitle?.Title ?? "Titel saknas"}
                    />
                </div>
                <div className="justify-start items-center border-b-1">
                    <p className="!text-[rgb(44,43,43)] text-lg !font-normal !pr-0 !mr-0">
                        {howToTitle?.Description}
                    </p>
                </div>
                <div className="flex">
                    <div className="w-5/20">
                        <div className="pt-2">
                            {groupedHowTosByCategory.map((group) => (
                                <div key={group.Category}>
                                    <button
                                        onClick={() => handleCategoryClick(group.Category)}
                                        className={` ${expandedCategory === group.Category ? '!bg-[#003d4c] text-white!' : ''} break-words w-full font-semibold! text-left p-1 text-lg hover:!bg-[#007167] rounded-md! border-none `}
                                        type="button"
                                    >
                                        {group.Category}
                                    </button>
                                    {expandedCategory === group.Category && (
                                        <div className="flex items-end flex-col">
                                            {group.Items.map((item) => (
                                                <button
                                                    type="button"
                                                    key={item.Id}
                                                    onClick={() => handleItemClick(item.Title, item.Id)}
                                                    className={`block
                                                    w-[95%] break-words text-left p-1 text-base hover:bg-gray-100 border-none transition-colors text-md hover:bg-[#eceae6] rounded-md!
                                                    ${item.Id === currentHowTo?.Id && 'bg-[#d4d5ce]'}
                                                    `}
                                                >
                                                    {item.Title}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {currentHowTo != null &&
                        <div className="w-15/20 border-l-1 ml-2!">
                            <div className="pl-10! pt-2">
                                <TitleComponent
                                    title={currentHowTo?.Title ?? ""}
                                />
                                <div className="
                                    flex 
                                    flex-col 
                                    !my-2
                                    !bg-[rgba(255,255,255,0.21)] 
                                    shadow-[0_4px_30px_rgba(0,0,0,0.1)] 
                                    !rounded-lg p-2 
                                    !text-[rgb(44,43,43)] 
                                ">
                                    <div className="
                                        flex 
                                        flex-col 
                                        w-full
                                        rounded-lg  
                                        p-2!
                                        shadow-[0_10px_20px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.12)]!
                                    ">
                                        <div className="w-full text-xl font-bold pb-2">Fråga</div>
                                        <p className="w-full text-base">
                                            {currentHowTo?.Question}
                                        </p>
                                    </div>
                                </div>
                                <div className="
                                    flex 
                                    flex-col 
                                    !my-2
                                    !bg-[rgba(255,255,255,0.21)] 
                                    shadow-[0_4px_30px_rgba(0,0,0,0.1)] 
                                    !rounded-lg p-2 
                                    !text-[rgb(44,43,43)] 
                                ">
                                    <div className="
                                        flex 
                                        flex-col 
                                        w-full
                                        rounded-lg  
                                        p-2!
                                        shadow-[0_10px_20px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.12)]!
                                    ">

                                        <div className="w-full text-xl font-bold pb-2">Svar</div>
                                        <div >
                                            <p className="w-full text-base whitespace-pre-wrap mb-5">
                                                {currentHowTo?.Answer}
                                            </p>
                                            {currentHowToImages && currentHowToImages.length > 0 &&
                                                currentHowToImages.map((image, index) => (
                                                    <div key={index}>
                                                        <img
                                                            src={env.imageUrl + image.FileRef}
                                                            style={{ maxHeight: `${image.ImgHeight}px` }}
                                                            className="max-w-[100%]! border-1 border-black"
                                                            alt={image.FileLeafRef?? ""}
                                                            title={image.FileLeafRef?? ""}
                                                        />
                                                        <p className="text-base mb-5">{image.ImageDescription}</p>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default HowTo;